import React, { FC, ReactNode } from "react";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { Grid, useMediaQuery, Box, Container, Typography } from "@mui/material";

interface StepItem {
  title: string;
  subTitle: string;
  icon: ReactNode;
}

const workSteps: Array<StepItem> = [
  {
    title: "Create Your gOGig Profile",
    subTitle: "Sign up for free and become part of a growing gig community that verifies transit ads across cities.",
    icon: (
      <AccountCircleOutlinedIcon color="primary" sx={{ fontSize: "32px" }} />
    ),
  },
  {
    title: "Find Gigs Around You",
    subTitle: "Browse available verification gigs near your location — auto branding, cab branding, and more.",
    icon: <ZoomInIcon color="primary" sx={{ fontSize: "32px" }} />,
  },
  {
    title: "Complete Gigs Your Way",
    subTitle:
      "Head to the gig location, snap photos, follow simple checklist steps, and submit your report.",
    icon: <VerifiedOutlinedIcon color="primary" sx={{ fontSize: "32px" }} />,
  },
  {
    title: "Get Paid for Every Verified Gig",
    subTitle:
      "Once your submission is approved, your payment lands in your wallet. Fast, secure, and hustle-friendly.",
    icon: <CurrencyRupeeIcon color="primary" sx={{ fontSize: "32px" }} />,
  },
];

const WorkProcessItem = ({
  step,
  isEven,
  isLast,
}: {
  step: StepItem;
  isEven: boolean;
  isLast: boolean;
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)"); // Adjust the tablet breakpoint as needed

  const primaryTextStyle = {
    fontWeight: "bold",
    display: "block",
    fontSize: "22px",
    fontFamily: "inter, sans-serif",
  };

  const secondaryTextStyle = {
    fontWeight: "normal",
    display: "block",
    fontSize: "17px",
    marginTop: "8px",
    fontFamily: "inter, sans-serif",
    color: "#767F8C",
  };
  return (
    <Box sx={{ position: "relative", textAlign: "center", padding: 2 }}>
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "relative",
      zIndex: 2  // Ensures content is above arrows
    }}
  >
    <Box
      sx={{
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        backgroundColor: "background.default",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 3,
        transition: "transform 0.3s ease",
        "&:hover": {
          transform: "scale(1.05)"
        }
      }}
    >
      {step.icon}
    </Box>
    <Typography 
      sx={{
        ...primaryTextStyle,
        fontWeight: "bold",
        mb: 1
      }}
    >
      {step.title}
    </Typography>
    <Typography 
      sx={{
        ...secondaryTextStyle,
        maxWidth: "220px",
        mx: "auto"
      }}
    >
      {step.subTitle}
    </Typography>
  </Box>
  
  {!isMobile && !isTablet && !isLast && (
    <Box
      sx={{
        position: "absolute",
        width: "75%",
        top: isEven ? "5%" : "25%",
        left: "65%",
        zIndex: 1,
        pointerEvents: "none"  // Prevents arrow from interfering with clicks
      }}
    >
      <img
        style={{
          width: "100%",
          height: "auto"
        }}
        src={
          isEven
            ? `${process.env.PUBLIC_URL}/images/home/arrows.png`
            : `${process.env.PUBLIC_URL}/images/home/arrow-reverse.png`
        }
        alt={isEven ? "Next step arrow" : "Reverse arrow"}
      />
    </Box>
  )}
</Box>
  );
};

const WorkingProcess: FC = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        pt: isMobile ? 4 : { xs: 4, md: 4 },
        pb: isMobile ? 4 : { xs: 4, md: 4 },
        textAlign: "center",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography
            component="h2"
            sx={{
              position: "relative",
              fontSize: isMobile ? 24 : { xs: 40, md: 36 },
              letterSpacing: 1.5,
              fontWeight: "bold",
              lineHeight: 1.1,
            }}
          >
            How gOGig Work
          </Typography>
        </Box>
        <Grid container spacing={2} mt={4}>
          {workSteps.map((step, index) => (
            <Grid key={step.title} item xs={12} md={3}>
              <WorkProcessItem
                step={step}
                isEven={index % 2 === 0}
                isLast={index === workSteps.length - 1}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WorkingProcess;
